import { useState } from 'react'
import { Alert, Box, Button, Checkbox, Chip, FormControlLabel, IconButton, Popover, Snackbar, TextField, Tooltip, Typography } from '@mui/material'
import { Copy, Mail, Pencil, ShieldCheck, UserPlus, X } from 'lucide-react'
import { Eyebrow, Mono, Surface } from '@agribot/polygon-checks-ui'
import { Segmented } from '@agribot/polygon-checks-ui'
import { SkeletonCards } from '@agribot/polygon-checks-ui'
import { FarmerPicker, SupplierPicker, type RecordOption } from '@agribot/polygon-checks-ui'
import { useSession } from '@agribot/polygon-checks-ui'
import { formatDate } from '@agribot/polygon-checks-ui'
import { tokens } from '@agribot/polygon-checks-ui'
import { STAFF_ROLES, useOrgUserMutations, useOrgUsers, type OrgUser, type StaffRole } from './hooks'

const KINDS = [
  { value: 'staff', label: 'Your organisation' },
  { value: 'supplier', label: 'A supplier' },
  { value: 'farmer', label: 'A farmer' },
] as const

export interface UserAdminPanelProps {
  /**
   * Offer to invite your organisation's own people. On (the default) where
   * staff sign in through us (Auth0). Off for an integrator that signs its
   * own staff in and provisions them with its root key (`POST /users`):
   * invitations are then for suppliers' and farmers' logins only, and staff
   * appear here once provisioned, for their roles to be set.
   */
  staffInvites?: boolean
  /** The heading and lead paragraph. Off when the host's page has its own. */
  heading?: boolean
}

const kindLabel = (u: OrgUser) =>
  u.kind === 'supplier' ? `Supplier · ${u.suppliers.map((s) => s.name).join(', ')}`
  : u.kind === 'farmer' ? `Farmer · ${u.farmers.map((f) => f.code ?? f.name).join(', ')}`
  : u.is_org_admin ? 'Your organisation · admin' : 'Your organisation'

/**
 * People with a login, and whose login it is: the organisation's own, or a
 * supplier's or farmer's, who then see only what is theirs. Their staff
 * roles, supplier admins, and sign-in links for suppliers. Inviting writes
 * the account: a supplier is emailed a sign-in link to the portal; staff
 * sign in through Auth0 with the invited address.
 *
 * Content only, no page shell: it fills the box it is given. Needs the API
 * configured and a react-query client around it, as inside `<PolygonChecks>`
 * or `<UserAdmin>`. Shown to an administrator of the organisation; the API
 * refuses anyone else.
 */
export function UserAdminPanel({ staffInvites = true, heading = true }: UserAdminPanelProps) {
  const { data: users = [], isLoading } = useOrgUsers()
  const { data: me } = useSession()
  const { invite, change, remove, setRoles, setSupplierAdmin, sendLink } = useOrgUserMutations()
  const [error, setError] = useState<string | null>(null)
  const [link, setLink] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [editing, setEditing] = useState<string | null>(null)

  const cols = 'minmax(0,1.6fr) minmax(0,2fr) 120px 96px'
  return (
    <Box sx={{ flex: 1, overflowY: 'auto', px: 8, py: 7 }}>
        {heading && (
          <>
            <Eyebrow sx={{ mb: 2 }}>Admin</Eyebrow>
            <Typography variant="h1" sx={{ mb: 2 }}>People</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 5, maxWidth: 700 }}>
              Your organisation’s people see the regions shared with them, and do what their roles allow.
              A supplier’s or a farmer’s login sees only their own plots, regions and campaign answers.
            </Typography>
          </>
        )}
        {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>{error}</Alert>}

        <Invite
          staff={staffInvites}
          busy={invite.isPending}
          onInvite={(body) => invite.mutateAsync(body)
            // A supplier login signs in from a link we email; anyone else through Auth0, at the address shared here.
            .then((r) => (body.kind === 'supplier' ? setNotice(`Invited. ${body.email} has been emailed a sign-in link to the supplier portal.`) : setLink(r.sign_in_url)))
            .catch((e: Error) => { setError(e.message); throw e })}
        />

        <Surface sx={{ p: 0, mt: 4 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: cols, gap: 4, px: 5, py: 3, borderBottom: `1px solid ${tokens.color.ink200}` }}>
            <Eyebrow>Person</Eyebrow><Eyebrow>Login of</Eyebrow><Eyebrow>Status</Eyebrow><Box />
          </Box>
          {isLoading && <SkeletonCards columns={cols} />}
          {users.map((u) => (
            <Box key={u.id} sx={{ px: 5, py: 3, borderBottom: `1px solid ${tokens.color.ink200}`, '&:last-of-type': { borderBottom: 0 } }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: cols, gap: 4, alignItems: 'center' }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{u.full_name ?? u.email ?? 'Integration user'}</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>{u.email ?? (u.provisioned_by_key ? 'Created by your integration' : '')}</Typography>
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ color: u.kind === 'staff' ? 'text.secondary' : 'text.primary' }}>{kindLabel(u)}</Typography>
                  {u.kind === 'staff' && (
                    <RoleChips
                      user={u}
                      busy={setRoles.isPending}
                      editable={u.id !== me?.id || Boolean(me?.is_org_admin)}
                      onChange={(roles) => setRoles.mutateAsync({ id: u.id, roles }).catch((e: Error) => setError(e.message))}
                    />
                  )}
                  {u.kind === 'supplier' && (
                    <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                      <Chip
                        size="small"
                        icon={<ShieldCheck size={12} />}
                        label={u.is_supplier_admin ? 'Supplier admin' : 'Make supplier admin'}
                        variant={u.is_supplier_admin ? 'filled' : 'outlined'}
                        color={u.is_supplier_admin ? 'info' : 'default'}
                        onClick={() => setSupplierAdmin.mutateAsync({ id: u.id, on: !u.is_supplier_admin }).catch((e: Error) => setError(e.message))}
                        aria-pressed={Boolean(u.is_supplier_admin)}
                      />
                      <Chip
                        size="small"
                        icon={<Mail size={12} />}
                        label="Email a sign-in link"
                        variant="outlined"
                        disabled={sendLink.isPending || !u.email}
                        onClick={() => sendLink.mutateAsync(u.id).then(() => setNotice(`A sign-in link is on its way to ${u.email}.`)).catch((e: Error) => setError(e.message))}
                      />
                    </Box>
                  )}
                </Box>
                <Typography variant="caption" sx={{ color: u.signed_in || u.provisioned_by_key ? 'text.secondary' : tokens.color.warning }}>
                  {u.signed_in || u.provisioned_by_key ? 'Active' : `Invited ${u.invited_at ? formatDate(u.invited_at) : ''}`}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  {u.id !== me?.id && (
                    <>
                      <Tooltip title="Change whose login this is">
                        <IconButton size="small" aria-label={`Change ${u.email ?? u.id}`} onClick={() => setEditing(editing === u.id ? null : u.id)}>
                          <Pencil size={13} strokeWidth={1.5} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove this login">
                        <IconButton size="small" aria-label={`Remove ${u.email ?? u.id}`} disabled={remove.isPending}
                          onClick={() => remove.mutateAsync(u.id).catch((e: Error) => setError(e.message))}>
                          <X size={13} strokeWidth={1.5} />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
              </Box>
              {editing === u.id && (
                <Box sx={{ mt: 3 }}>
                  <KindChooser
                    initial={u}
                    busy={change.isPending}
                    action="Save"
                    onSubmit={(body) => change.mutateAsync({ id: u.id, ...body }).then(() => setEditing(null)).catch((e: Error) => { setError(e.message); throw e })}
                  />
                </Box>
              )}
            </Box>
          ))}
        </Surface>

      <Snackbar open={Boolean(notice)} autoHideDuration={6000} onClose={() => setNotice(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>
      </Snackbar>
      <Snackbar open={Boolean(link)} onClose={() => setLink(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setLink(null)}
          action={<IconButton size="small" aria-label="Copy sign-in link" onClick={() => link && navigator.clipboard.writeText(link)}><Copy size={14} /></IconButton>}>
          Invited. Send them this sign-in link: <Mono sx={{ fontSize: 12 }}>{link}</Mono>
        </Alert>
      </Snackbar>
    </Box>
  )
}

type Body = { kind: OrgUser['kind']; supplier_ids?: string[]; farmer_id?: string | null; locale?: string }

/**
 * The supplier portal's languages (the same list as @agribot/sourcing-model's
 * PORTAL_LANGUAGES, which this package does not depend on). An invited
 * supplier's portal and emails start in the one chosen; they can change it.
 */
const PORTAL_LANGUAGES = [
  { value: '', label: 'Their browser’s' },
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
  { value: 'pt-BR', label: 'Português (Brasil)' },
  { value: 'zh-CN', label: '简体中文' },
  { value: 'th', label: 'ไทย' },
  { value: 'ko', label: '한국어' },
]

/** Whose login: the organisation's, a supplier's (which supplier) or a farmer's (which farmer). */
function KindChooser({
  initial, busy, action, onSubmit, children, withLanguage = false, staff = true,
}: {
  initial?: OrgUser
  busy: boolean
  action: string
  onSubmit: (body: Body) => Promise<unknown>
  children?: React.ReactNode
  /** Offer the supplier portal's language, for a new supplier login. */
  withLanguage?: boolean
  /** Offer the organisation's own people as a kind. */
  staff?: boolean
}) {
  const kinds = staff ? [...KINDS] : KINDS.filter((k) => k.value !== 'staff')
  const [kind, setKind] = useState<OrgUser['kind']>(initial?.kind ?? kinds[0]!.value)
  const [locale, setLocale] = useState('')
  const [supplier, setSupplier] = useState<RecordOption | null>(
    initial?.suppliers[0] ? { id: initial.suppliers[0].id, label: initial.suppliers[0].name } : null,
  )
  const [farmer, setFarmer] = useState<RecordOption | null>(
    initial?.farmers[0] ? { id: initial.farmers[0].id, label: initial.farmers[0].code ?? initial.farmers[0].name ?? '' } : null,
  )
  const ready = kind === 'staff' || (kind === 'supplier' && supplier) || (kind === 'farmer' && farmer)
  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
      {children}
      <Segmented ariaLabel="Whose login" value={kind} onChange={(v) => setKind(v as OrgUser['kind'])} options={kinds} />
      {kind === 'supplier' && <Box sx={{ width: 260 }}><SupplierPicker value={supplier} onChange={setSupplier} countryCode={null} /></Box>}
      {kind === 'farmer' && <Box sx={{ width: 260 }}><FarmerPicker value={farmer} onChange={setFarmer} /></Box>}
      {kind === 'supplier' && withLanguage && (
        <TextField select size="small" label="Portal language" value={locale} onChange={(e) => setLocale(e.target.value)}
          InputLabelProps={{ shrink: true }} SelectProps={{ native: true }} sx={{ width: 190 }}>
          {PORTAL_LANGUAGES.map((l) => <option key={l.value} value={l.value} lang={l.value || undefined}>{l.label}</option>)}
        </TextField>
      )}
      <Button size="small" disabled={!ready || busy}
        onClick={() => {
          onSubmit({
            kind,
            ...(kind === 'supplier' && supplier ? { supplier_ids: [supplier.id] } : {}),
            ...(kind === 'farmer' && farmer ? { farmer_id: farmer.id } : {}),
            ...(kind === 'supplier' && withLanguage && locale ? { locale } : {}),
          }).catch(() => undefined)
        }}>
        {action}
      </Button>
    </Box>
  )
}

function Invite({ busy, staff, onInvite }: { busy: boolean; staff: boolean; onInvite: (body: Body & { email: string; full_name?: string }) => Promise<unknown> }) {
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [round, setRound] = useState(0)
  return (
    <Surface sx={{ p: 4 }}>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2.5 }}>
        <UserPlus size={15} strokeWidth={1.5} />
        <Eyebrow>Invite someone</Eyebrow>
      </Box>
      <KindChooser
        key={round}
        withLanguage
        staff={staff}
        busy={busy || !/^\S+@\S+\.\S+$/.test(email.trim())}
        action="Invite"
        onSubmit={(body) => onInvite({ ...body, email: email.trim(), ...(name.trim() ? { full_name: name.trim() } : {}) })
          .then(() => { setEmail(''); setName(''); setRound((r) => r + 1) })}
      >
        <TextField size="small" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com"
          inputProps={{ 'aria-label': 'Email to invite' }} sx={{ width: 260 }} />
        <TextField size="small" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (optional)"
          inputProps={{ 'aria-label': 'Their name' }} sx={{ width: 200 }} />
      </KindChooser>
    </Surface>
  )
}

/**
 * A member of staff's roles for supplier data collection, as chips; the
 * pencil opens the full list. An organisation admin holds every role
 * whatever is ticked, and the API says so.
 */
function RoleChips({ user, busy, editable, onChange }: {
  user: OrgUser
  busy: boolean
  editable: boolean
  onChange: (roles: StaffRole[]) => void
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const roles = user.roles ?? []
  const labelOf = (r: StaffRole) => STAFF_ROLES.find((x) => x.value === r)?.label ?? r
  return (
    <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
      {user.is_org_admin
        ? <Typography variant="caption" sx={{ color: 'text.secondary' }}>Every role, as an admin</Typography>
        : roles.length
          ? roles.map((r) => <Chip key={r} size="small" label={labelOf(r)} />)
          : <Typography variant="caption" sx={{ color: 'text.secondary' }}>No sourcing roles</Typography>}
      {editable && !user.is_org_admin && (
        <Tooltip title="Sourcing roles">
          <IconButton size="small" aria-label={`Roles of ${user.full_name ?? user.email ?? user.id}`} onClick={(e) => setAnchor(e.currentTarget)}>
            <Pencil size={12} strokeWidth={1.5} />
          </IconButton>
        </Tooltip>
      )}
      <Popover open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', width: 320 }}>
          {STAFF_ROLES.map((r) => (
            <FormControlLabel
              key={r.value}
              disabled={busy}
              control={(
                <Checkbox
                  checked={roles.includes(r.value)}
                  onChange={(_, on) => onChange(on ? [...roles, r.value] : roles.filter((x) => x !== r.value))}
                />
              )}
              label={(
                <Box>
                  <Box sx={{ font: `500 13px ${tokens.font.sans}` }}>{r.label}</Box>
                  <Box sx={{ font: `400 12px ${tokens.font.sans}`, color: 'text.secondary' }}>{r.help}</Box>
                </Box>
              )}
              sx={{ alignItems: 'flex-start', my: 1, '& .MuiCheckbox-root': { pt: 0.5 } }}
            />
          ))}
        </Box>
      </Popover>
    </Box>
  )
}
